import { ApplicationController, randomId, raw } from '@aha-app/mvc';
import {
  Editor,
  Range,
  type NodeEntry,
  type Selection,
  type Element,
  type Operation,
} from 'slate';
import * as Sentry from '@sentry/browser';
import AhaEditor, { type NodeDecorationsMap } from '../AhaEditor';
import CollaborationPlugin from '../collaboration/CollaborationPlugin';
import type {
  EditorMetadata,
  CollaborationOptions,
  PluginState,
  EditorPluginList,
  DocumentValue,
  CollaborationConfiguration,
  RecordId,
  ElementToolbarOptions,
} from '../types/aha-editor';
import { makeAnnotationRangeRefs } from '../ot-slate-v2';
import type Tool from '../tools/Tool';
import type DocumentWithPresence from '../collaboration/DocumentWithPresence';
import type { FetchUserInfo } from '../collaboration/DocumentWithPresence';
import EditorUtils from '../AhaEditorUtils/EditorUtils';
import History from '../collaboration/History';
import CommandMenuPlugin from '../plugins/CommandMenuPlugin/CommandMenuPlugin';
import { ReactEditor } from 'slate-react';
import type { HtmlSerializerOptions } from '../serialization/HtmlSerializer';
import OperationsUtils from '../AhaEditorUtils/OperationsUtils';
import { type EditableProps as SlateEditableProps } from 'slate-react/dist/components/editable';

export interface Behaviors {
  /**
   * Shows the border around the editor, even when blurred
   *
   * - `true` and `'always'` will always show the border (often used in forms/modals)
   * - `false` and `'never'` will never show the border (used in Discovery)
   * - `'hovered'`: only show the border when the editor is focused (used in most places in-app)
   */
  showBorder?: boolean | 'focused';

  // This is default behaviour in most of the app: the toolbar is hidden when blurred, and clicking
  // into the editor does not shift the content, instead the toolbar is shown above the editor.
  inline?: boolean;

  // This mode is used to just hide the toolbar when blurred, but it will shift the content down when focused.
  hiddenToolbar?: boolean;

  // Minimum height of the editor, useful for forms/modals when we want the editor to be a little taller.
  minimumLines?: number;

  // Text to show as placeholder if the editor is empty.
  placeholder?: string;

  // React children to show as placeholder if the editor is empty. Overrides `placeholder` if set.
  placeholderChildren?: React.ReactNode;

  // Show toolbars for the current selection. The position determines whether
  // the toolbar appears in the gutter to the right of the text, or appears
  // directly above the selection.
  selectionToolbars?: {
    tools: (typeof Tool)[];
    position: 'above' | 'right';
  }[];

  // Whether the editor should automatically scroll the selection into view.
  // Pass `true` to disable auto-scrolling.
  disableAutoScroll?: boolean;

  // Whether to focus the editor on initial mount, useful in forms/modals
  focusOnMount?: boolean;
}

export interface AhaEditorV2ControllerProps {
  editor?: Editor;
  plugins: EditorPluginList;
  commandMenuTools?: (typeof Tool)[][];
  serializerOptions?: HtmlSerializerOptions;
  value: DocumentValue;
  collaborationOptions?: CollaborationOptions;
  fetchUserInfo?: FetchUserInfo;
  readOnly?: boolean;
  annotateOnly?: boolean;
  interactive?: boolean;
  uploadMediaFile?: (file: File) => Promise<string | null | undefined>;
  metadata?: EditorMetadata;
  objectType?: string;
  objectId?: RecordId;
  baseURL?: string;
  behaviors?: Behaviors;
  historyURL?: string;
  parentType?: string;
  parentId?: RecordId;
  pageId?: RecordId;

  // Element toolbar options
  elementToolbarOptions?: ElementToolbarOptions;
}

type EditableProps = Pick<
  SlateEditableProps,
  'placeholder' | 'scrollSelectionIntoView'
>;

export type ToolbarCompactLevel = 'normal' | 'compact' | 'ultra-compact';

interface AhaEditorV2ControllerState {
  editableProps: EditableProps;
  readOnly: boolean;
  toolbarRenderId: string;
  editorRenderId: string;
  clipboardMoveId: string;
  toolbarCompactLevel: ToolbarCompactLevel;
  toolbarIsFaded: boolean;
  pluginState: PluginState;
  annotateOnly: boolean;
  interactive: boolean;

  minimumLines?: number;

  // Whether any part of the editor (including toolbars) is focused.
  // Do not use this variable directly. use `useAhaEditorFocused` instead.
  focused: boolean;

  // Whether an element within the editor (ex, image, etc) is currently being resized
  isResizing: boolean;
}

const MOVE_ID_TYPE = 'application/x-aha-v2-move-id';

class AhaEditorV2Controller extends ApplicationController<
  AhaEditorV2ControllerState,
  AhaEditorV2ControllerProps
> {
  behaviors: Behaviors;
  editor: Editor;
  ahaEditor: AhaEditor;
  plugins: EditorPluginList;
  collaborationOptions?: CollaborationConfiguration;
  collaborationPlugin?: ReturnType<typeof CollaborationPlugin>;
  collaborativeDocument?: DocumentWithPresence;
  editorOnChange: Editor['onChange'];
  documentVersion: number;
  decorations: NodeDecorationsMap;
  currentSelection: Selection | null;
  toolbarFadeTimer: ReturnType<typeof setTimeout> | null;
  serializerOptions: HtmlSerializerOptions;
  editorContainer: HTMLDivElement;
  toolbarContainer: HTMLDivElement;
  collaborationReady: boolean;
  collaborativeDocumentLoaded: boolean;

  static initialState: Partial<AhaEditorV2ControllerState> = {
    editableProps: {},
    toolbarCompactLevel: 'normal',
    toolbarIsFaded: true,
    focused: false,
    isResizing: false,
  };

  selectionUpdated() {
    const { selection } = this.editor;

    if (
      selection &&
      (!this.currentSelection ||
        !Range.equals(this.currentSelection, selection))
    ) {
      this.currentSelection = selection;
      return true;
    }

    return false;
  }

  async initialize({
    plugins,
    commandMenuTools,
    collaborationOptions,
    value,
    editor,
    readOnly,
    annotateOnly,
    interactive = true,
    serializerOptions,
    behaviors = {},
  }: AhaEditorV2ControllerProps) {
    // We should never have a proxied value passed in. This can cause issues w/ Slate's internal storage of elements.
    if (value) {
      if (raw(value) !== value) {
        console.error('Detected proxied editor value');
        Sentry.captureException('Detected proxied editor value');
        value = raw(value);
      }

      if (raw(value.children) !== value.children) {
        console.error('Detected proxied editor.children value');
        Sentry.captureException('Detected proxied editor.children value');
        value.children = raw(value.children);
      }
    }

    this.decorations = new Map();
    this.editor = editor ?? EditorUtils.createReactEditor();
    this.plugins = plugins;
    this.collaborationReady = false;
    this.collaborativeDocumentLoaded = false;
    if (collaborationOptions?.enabled)
      this.collaborationOptions = collaborationOptions;
    this.currentSelection = null;
    this.state.readOnly = Boolean(readOnly);
    this.state.annotateOnly = Boolean(annotateOnly);
    this.state.interactive = interactive;
    this.state.toolbarRenderId = '';
    this.state.editorRenderId = '';
    this.state.pluginState = {};
    this.serializerOptions = serializerOptions ?? {};
    this.behaviors = behaviors;

    this.initializeEditor(value, plugins, commandMenuTools);
    this.initializeCollaboration();
    this.initializeHistory();
    this.initializeBehaviors(behaviors);
    this.initializeToolbarContainer();
  }

  private initializeEditor(
    value: DocumentValue,
    plugins: EditorPluginList,
    commandMenuTools?: (typeof Tool)[][]
  ) {
    this.ahaEditor = new AhaEditor(this.editor, this.proxiedThis);

    if (Array.isArray(commandMenuTools) && commandMenuTools.length > 0) {
      this.plugins.unshift(CommandMenuPlugin(commandMenuTools));
    }

    // Metadata must be initialized before plugins.
    this.initializeMetadata(value.metadata);

    // Initialize Editor plugins
    if (plugins) {
      this.ahaEditor.register(plugins.flat(10));
    }

    // Initialize editor value
    this.ahaEditor.editor.children = value.children;

    Editor.normalize(this.editor, { force: true });

    this.editorOnChange = this.editor.onChange;
    this.editor.onChange = (change) => {
      this.computeDecorations();
      this.editorOnChange(change);

      if (
        this.ahaEditor.handlers.onSelectionUpdate.length > 0 &&
        this.selectionUpdated()
      ) {
        for (const handler of this.ahaEditor.handlers.onSelectionUpdate) {
          handler(this.ahaEditor);
        }
      }

      if (this.isCollaborative) {
        this.collaborationPlugin?.api.apply(this.collaborativeDocument!, {
          operations: this.editor.operations
            // Don't resend collaborative operations, and mark new operations
            // as collaborative so that they are processed only once
            .filter((op) => !op.collaborative)
            .map((op) => ({ ...op, collaborative: true })),
        });
      }

      this.ahaEditor.runOnChangeHandlers();
    };

    this.computeDecorations();
  }

  private initializeMetadata(metadata: EditorMetadata | undefined) {
    // Set the metadata to an empty object with annotations and tableOfContents if it is undefined.
    this.editor.metadata = metadata || EditorUtils.defaultMetadata();

    // Set just the tableOfContents if it is undefined.
    if (!this.editor.metadata.tableOfContentsEnabled) {
      this.editor.metadata.tableOfContentsEnabled = false;
    }

    // Set just the annotations if it is undefined.
    if (!this.editor.metadata.annotations) {
      this.editor.metadata.annotations = {};
    }

    makeAnnotationRangeRefs(this.editor);
  }

  private initializeBehaviors(behaviors: Behaviors) {
    if (Object.hasOwn(behaviors, 'placeholder')) {
      this.state.editableProps.placeholder = behaviors.placeholder;
    } else if (Object.hasOwn(behaviors, 'placeholderChildren')) {
      // Slate will not call renderPlaceholder if placeholder is null,
      // so we need to set it to something
      this.state.editableProps.placeholder = ' ';
    }

    if (behaviors.disableAutoScroll) {
      this.state.editableProps.scrollSelectionIntoView = () => {};
    }
  }

  private initializeToolbarContainer() {
    const toolbarContainer = document.createElement('div');
    toolbarContainer.dataset.skipDrawerClose = '';
    document.body.appendChild(toolbarContainer);

    this.toolbarContainer = toolbarContainer;
  }

  actionSetContainerElement(container: HTMLDivElement) {
    this.editorContainer = container;
  }

  actionSetPluginState(property: keyof PluginState, value: any) {
    this.state.pluginState[property] = value;
  }

  /**
   * Initializes collaborative editing features for the editor.
   * Sets up collaboration plugin and configures Sentry tags for debugging.
   * @throws {Error} If collaboration options are invalid or missing required properties
   */
  private initializeCollaboration() {
    const { collaborationOptions } = this;
    if (!collaborationOptions?.enabled) return;

    if (!Object.hasOwn(collaborationOptions, 'enabled')) {
      throw new Error('Collaboration options must have an "enabled" property');
    }

    if (!collaborationOptions.enabled) return;

    if (
      !Object.hasOwn(collaborationOptions, 'documentVersion') ||
      !Object.hasOwn(collaborationOptions, 'connectionHandler')
    ) {
      throw new Error(
        'Collaboration options must have "documentVersion" and "connectionHandler" properties.'
      );
    }

    const { documentVersion, connectionHandler } = collaborationOptions;
    if (connectionHandler && typeof connectionHandler !== 'function') {
      throw new Error('Connection handler must be a function.');
    }

    if (connectionHandler) {
      collaborationOptions.onConnect = () => {
        connectionHandler(() => this.openCollaborativeDocument());
      };
    } else {
      collaborationOptions.onConnect = () => {
        this.openCollaborativeDocument();
      };
    }

    const plugin = CollaborationPlugin(collaborationOptions);
    this.ahaEditor.register(plugin);
    this.collaborationPlugin = plugin;
    if (documentVersion) {
      this.documentVersion = documentVersion;
    }
    Sentry.setTag('DocumentId', collaborationOptions.documentId);

    plugin.api.init();
  }

  private initializeHistory() {
    History.setForEditor(this.editor, new History(this.collaborativeDocument));
  }

  onUpdate = (editor: Editor, version: number) => {
    this.documentVersion = version;
    this.editorOnChange();
  };

  loadCollaborativeDocument() {
    if (!this.collaborationPlugin) {
      return;
    }

    const { documentCollection, documentId, documentVersion, userInfo } =
      this.collaborationOptions!;
    const provider = this.ahaEditor.getProvider('documentState');

    this.collaborativeDocument = this.collaborationPlugin?.api.openDocument(
      documentCollection,
      documentId,
      this.editor,
      documentVersion,
      provider?.onDocumentStateChanged,
      this.onUpdate
    );

    this.collaborationReady = true;
    this.collaborativeDocumentLoaded = true;

    if (userInfo) {
      this.collaborativeDocument.setUserInfo(userInfo);
    }
  }

  openCollaborativeDocument() {
    if (!this.collaborativeDocument) {
      this.loadCollaborativeDocument();
    }
    this.collaborationPlugin?.api.connectDocument(this.collaborativeDocument!);
  }

  canApply(operation: Operation): boolean {
    if (!this.isCollaborative) return true;

    if (!this.isModifiable && OperationsUtils.isModifyingOperation(operation)) {
      return false;
    }

    // Normalizations are applied before initializing collaboration,
    // we want to allow those always.
    if (!this.collaborationPlugin?.api.isInitialized()) return true;

    return this.collaborationPlugin?.api.canApply(this.collaborativeDocument!);
  }

  onDOMBeforeInput = (event: InputEvent): void => {
    if (!this.isModifiable) {
      event.preventDefault();
    }
  };

  decorate([node, _]: NodeEntry<Element>) {
    return this.decorations.get(node) ?? [];
  }

  eventHandlers() {
    return this.ahaEditor.eventHandlers;
  }

  editorContainerEventHandlers() {
    return this.ahaEditor.editorContainerEventHandlers;
  }

  dispatchEvent(eventName: string, detail = {}) {
    if (!this.editorContainer) {
      console.error(
        'Cannot dispatchEvent for editor, editorContainer is null.'
      );
      return;
    }

    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: {
        container: this.editorContainer,
        controller: this,
        ...detail,
      },
    });

    this.editorContainer.dispatchEvent(event);
  }

  actionUpdateReadOnly(readOnly?: boolean) {
    const newReadOnly = Boolean(readOnly);
    if (newReadOnly !== this.state.readOnly) {
      this.state.readOnly = newReadOnly;
    }
  }

  actionSetResizing(isResizing: boolean) {
    if (this.state.isResizing !== isResizing) {
      this.state.isResizing = isResizing;
    }
  }

  changeProps(newProps: AhaEditorV2ControllerProps) {
    this.actionUpdateReadOnly(newProps.readOnly);

    this.state.minimumLines = newProps.behaviors?.minimumLines;
  }

  actionReRenderToolbar() {
    this.state.toolbarRenderId = String(Math.floor(Math.random() * 10000000));
  }

  actionReRenderEditor() {
    this.state.editorRenderId = String(Math.floor(Math.random() * 10000000));
  }

  get hasSelections() {
    return (
      this.collaborationReady &&
      this.collaborativeDocumentLoaded &&
      !this.state.readOnly
    );
  }

  destroy() {
    if (this.collaborativeDocument) {
      this.collaborationPlugin?.api.closeDocument(this.collaborativeDocument);
    }
    if (this.toolbarFadeTimer) {
      clearTimeout(this.toolbarFadeTimer);
    }

    // Destroy the editor
    if (this.ahaEditor) {
      this.ahaEditor.destroy();
    }

    this.toolbarContainer.remove();
  }

  actionSetToolbarCompactLevel(level: ToolbarCompactLevel) {
    if (this.state.toolbarCompactLevel !== level) {
      this.state.toolbarCompactLevel = level;
      this.actionFadeInToolbar();
    }
  }

  // NOTE: Backward compatibility method - maps boolean to compact levels
  actionSetCompactToolbar(compact: boolean) {
    const level: ToolbarCompactLevel = compact ? 'compact' : 'normal';
    this.actionSetToolbarCompactLevel(level);
  }

  actionSetMoveId(data: DataTransfer | null) {
    if (!data) return;

    const moveId = randomId();

    this.state.clipboardMoveId = moveId;
    data.setData(MOVE_ID_TYPE, moveId);
  }

  isMove(data: DataTransfer | null): boolean {
    if (!data) return false;

    try {
      const moveId = data.getData(MOVE_ID_TYPE);
      return Boolean(moveId && moveId === this.state.clipboardMoveId);
    } catch {
      // If we can't access the data (e.g., security restrictions),
      // assume it's not internal
      return false;
    }
  }

  actionClearMoveId() {
    this.state.clipboardMoveId = '';
  }

  actionFadeInToolbar() {
    this.state.toolbarIsFaded = true;

    if (this.toolbarFadeTimer) {
      clearTimeout(this.toolbarFadeTimer);
    }

    this.toolbarFadeTimer = setTimeout(() => {
      this.state.toolbarIsFaded = false;
    }, 10);
  }

  get baseURL() {
    return this.collaborationOptions?.collaborationBaseURL ?? '';
  }

  get historyURL() {
    return this.props.historyURL;
  }

  get getSharingToken() {
    if (!this.props.collaborationOptions?.enabled) return;

    return this.props.collaborationOptions.getSharingToken;
  }

  get objectType() {
    return this.props.objectType;
  }

  get objectId() {
    return this.props.objectId;
  }

  private computeDecorations() {
    this.decorations = this.ahaEditor.computeDecorations();
  }

  actionForceComputeDecorations() {
    this.computeDecorations();
    this.actionReRenderEditor();
  }

  actionRunOnMountHandlers(): (() => void)[] {
    if (this.props.behaviors?.focusOnMount) {
      ReactEditor.focus(this.ahaEditor.editor);
    }

    return this.ahaEditor.handlers.onMount
      .map((handler) => handler(this.ahaEditor))
      .filter((handler) => typeof handler === 'function')
      .toReversed();
  }

  hasTableSelection(): boolean {
    const { tableSelection } = this.state.pluginState;
    if (!tableSelection) return false;

    return tableSelection.multipleCellsSelected;
  }

  get isCollaborative() {
    return this.collaborationOptions?.enabled;
  }

  get isModifiable() {
    return !this.state.readOnly && !this.state.annotateOnly;
  }
}

export default AhaEditorV2Controller;
